// Standardised, filesystem-safe export filenames (MP-OS-190). Pattern: report type,
// supplier/brand DISPLAY NAME (never a manufacturer/tenant id or UUID — callers pass
// ACTIVE_SUPPLIER.label / SCOPE, never TENANT.manufacturer), the selected segment and a
// short filter slug where relevant (MP-OS-210), then the period. Mirrors the backend builder
// (app/api/main.py _export_filename) so a client-named download and a server
// Content-Disposition agree for the same export.

const FIELD_MAX = 40
const NAME_MAX = 150
const FILTER_SLUG_MAX = 60
const FALLBACK = 'unknown'

// å/ä/ö etc. decompose under NFKD into a base letter + combining diacritic; stripping the
// combining range (U+0300–U+036f) after normalising transliterates them consistently
// without a hand-maintained character map.
function slugField(value: string | null | undefined, fallback: string = FALLBACK): string {
  if (!value) return fallback
  const ascii = value.toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '')
  const slug = ascii.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, FIELD_MAX).replace(/-+$/g, '')
  return slug || fallback
}

// The one list of English report types (MP-OS-190): every file the dashboard produces names
// itself with one of these, so no export can fall back to a Swedish or scope-less name.
export const REPORT_TYPES = {
  salesTrend: 'minprilla-sales-trend',
  articles: 'minprilla-articles',
  monthlyReport: 'minprilla-monthly-report',
  dashboard: 'minprilla-dashboard',
} as const
export type ReportType = (typeof REPORT_TYPES)[keyof typeof REPORT_TYPES]

// The admin's whole-market view has no supplier; it gets a clear scope word instead of a
// sentence like "The market". An admin preview passes the previewed supplier's label.
export const WHOLE_MARKET_SCOPE = 'whole-market'

/** The scope field for an export: the supplier's DISPLAY NAME, or WHOLE_MARKET_SCOPE when no
 * supplier is selected. Callers pass TENANT.manufacturer and ACTIVE_SUPPLIER.label. */
export function exportScope(manufacturer: string | null | undefined, label: string | null | undefined): string {
  return manufacturer ? label || '' : WHOLE_MARKET_SCOPE
}

/** One active filter for a file name: its key (brand, strength, format, …) and shown value. */
export interface ExportFilenameFilter {
  key: string
  value: string
}

/** Short ASCII slug of the active filters, e.g. `format-mini_flavour-mint` (MP-OS-210). Bounded,
 * so many filters never push the period out of the name. Mirrors main.py _filter_slug. */
export function filterSlug(filters: readonly ExportFilenameFilter[]): string {
  return filters.filter((f) => f.value).map((f) => `${slugField(f.key)}-${slugField(f.value)}`)
    .join('_').slice(0, FILTER_SLUG_MAX).replace(/[-_]+$/g, '')
}

export interface ExportFilenameParts {
  reportType: ReportType
  scope: string // supplier or brand DISPLAY NAME — never a manufacturer/tenant id or UUID
  title?: string | null // a user-chosen name (a dashboard's), after the scope
  segment?: string | null // omitted when the export isn't scoped to one segment
  filters?: readonly ExportFilenameFilter[] // the active filters, as a short slug after the segment
  from?: string | null // ISO date
  to?: string | null // ISO date
  extension: string
}

export function buildExportFilename(parts: ExportFilenameParts): string {
  const fields = [slugField(parts.reportType, parts.reportType), slugField(parts.scope)]
  if (parts.title) fields.push(slugField(parts.title))
  if (parts.segment) fields.push(slugField(parts.segment))
  const filters = filterSlug(parts.filters ?? [])
  if (filters) fields.push(filters)
  if (parts.from) fields.push(slugField(parts.from))
  if (parts.to) fields.push(slugField(parts.to))
  const name = fields.join('_').slice(0, NAME_MAX).replace(/_+$/g, '')
  return `${name}.${parts.extension}`
}
